// frontend/src/components/chat/ConversationDebugLogs.js
import React, { useState, useEffect } from 'react';
import { sanitizeInlineHTML } from '../../utils/sanitize';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Badge,
  Card,
  CardContent,
  Divider,
  Stack,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import FunctionsIcon from '@mui/icons-material/Functions';
import StorageIcon from '@mui/icons-material/Storage';
import DownloadIcon from '@mui/icons-material/Download';
import ArticleIcon from '@mui/icons-material/Article';
import HistoryIcon from '@mui/icons-material/History';
import axios from 'axios';

const ConversationDebugLogs = ({ conversationId, companyId, messageId, open, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedIteration, setExpandedIteration] = useState(null);
  const [messageList, setMessageList] = useState([]);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [debugSettings, setDebugSettings] = useState({ enabled: true, visible_log_types: [] });

  // Helper function to format timestamp in user's local timezone
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Parse the timestamp - handle both ISO string and other formats
    let date = new Date(timestamp);
    
    // If the timestamp is a string without 'Z' or timezone, treat it as UTC
    if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
      date = new Date(timestamp + 'Z');
    }
    
    // Format in user's local timezone
    const options = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    
    return date.toLocaleString(undefined, options); // undefined uses browser's locale
  };

  useEffect(() => {
    if (conversationId && companyId && open) {
      fetchDebugSettings();
      fetchMessageList();
    }
  }, [conversationId, companyId, open]);

  useEffect(() => {
    if (conversationId && companyId && open) {
      fetchLogs();
    }
  }, [conversationId, companyId, selectedMessageId, messageId, open]);
  
  const fetchDebugSettings = async () => {
    try {
      const response = await axios.get(`/api/admin/debug-logs/settings/${companyId}`);
      if (response.data?.success) {
        setDebugSettings(response.data.settings);
      }
    } catch (e) {
      console.error('Error fetching debug settings:', e);
      // Default to showing all log types if settings fetch fails
      setDebugSettings({ enabled: true, visible_log_types: [] });
    }
  };

  const fetchMessageList = async () => {
    try {
      const resp = await axios.get(`/api/chat/logs/${conversationId}/messages`, {
        params: { company_id: companyId },
        withCredentials: true,
      });
      if (resp.data?.success) {
        const items = resp.data.messages || [];
        setMessageList(items);
        // Default selection: incoming messageId prop, else most recent item, else null (fallback to full conv)
        const initial = messageId || items[0]?.message_id || null;
        setSelectedMessageId(initial);
      }
    } catch (e) {
      console.error('Error fetching message list:', e);
      setMessageList([]);
      setSelectedMessageId(null);
    }
  };

  const fetchLogs = async () => {
    if (!conversationId || !companyId) {
      console.log('[Debug Logs] Waiting for conversationId...', { conversationId, companyId });
      setError('Waiting for conversation ID...');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // CRITICAL FIX: Always fetch ALL logs if message list hasn't been populated yet
      // This ensures we can derive the message list from logs and show the dropdown
      const shouldFetchAll = (messageList?.length || 0) === 0;
      
      // Only filter by message_id if:
      // 1. Message list has been populated (dropdown is ready), AND
      // 2. A specific message is selected
      const mid = shouldFetchAll ? null : selectedMessageId;
      
      console.log('[Debug Logs] Fetch params:', {
        conversationId,
        messageListEmpty: shouldFetchAll,
        messageListLength: messageList?.length || 0,
        selectedMessageId,
        messageIdProp: messageId,
        finalMid: mid,
        willFetchAll: !mid
      });
      
      // Build params conditionally: only add message_id filter if explicitly requested
      const params = { company_id: companyId };
      if (mid) {
        params.message_id = mid;
        console.log('[Debug Logs] Filtering by message_id:', mid);
      } else {
        console.log('[Debug Logs] Fetching ALL logs for conversation');
      }

      // Use backend API proxy to avoid CORS issues
      const response = await axios.get(
        `/api/chat/logs/${conversationId}`,
        {
          params,
          withCredentials: true,
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'x-auth-token': localStorage.getItem('token')
          }
        }
      );

      if (response.data.success) {
        const fetchedLogs = response.data.logs || [];
        setLogs(fetchedLogs);
        
        // DEBUG: Check if logs have message_id
        console.log('[Debug Logs] Fetched logs sample:', fetchedLogs.slice(0, 2).map(l => ({
          message_id: l.message_id,
          log_type: l.log_type,
          has_message_id: !!l.message_id
        })));
        console.log('[Debug Logs] messageList.length:', messageList.length);

        // Fallback: if the messages endpoint returned nothing, derive a dropdown from logs
        // This always runs when messageList is empty to ensure the dropdown is populated
        if ((messageList?.length || 0) === 0 && fetchedLogs.length > 0) {
          try {
            console.log('[Debug Logs] Deriving message list from logs...');
            const byId = new Map();
            for (const log of fetchedLogs) {
              const msgId = log.message_id || log.messageId;
              if (!msgId || msgId === '' || msgId === 'msgid') continue; // Skip invalid message IDs
              if (!byId.has(msgId)) {
                // Extract user message text from the log
                let text = '';
                if (log.log_type === 'message' && log.log_subtype === 'user_message') {
                  text = log.content?.message || '[no text]';
                }
                byId.set(msgId, {
                  message_id: msgId,
                  message: text || '[no text]',
                  timestamp: log.timestamp
                });
              } else {
                // Update message text if we find a better one (from message log)
                const existing = byId.get(msgId);
                if (log.log_type === 'message' && log.log_subtype === 'user_message' && log.content?.message) {
                  existing.message = log.content.message;
                }
              }
            }
            const derived = Array.from(byId.values())
              .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
            if (derived.length > 0) {
              console.log(`[Debug Logs] Derived ${derived.length} messages from logs:`, derived.map(d => ({ id: d.message_id.substring(0, 8), msg: d.message.substring(0, 30) })));
              setMessageList(derived);
              // If we had a messageId prop but no selected message yet, set it
              // This will trigger a re-fetch with the filter (via useEffect dependency)
              if (!selectedMessageId && messageId && derived.some(d => d.message_id === messageId)) {
                console.log('[Debug Logs] Setting selectedMessageId from prop:', messageId);
                setSelectedMessageId(messageId);
              } else if (!selectedMessageId) {
                // Default to most recent message if no selection
                console.log('[Debug Logs] Defaulting to most recent message:', derived[0].message_id.substring(0, 8));
                setSelectedMessageId(derived[0].message_id);
              }
            }
          } catch (e) {
            console.warn('Failed to derive message list from logs:', e);
          }
        }
      } else {
        setError(response.data.error || 'Failed to fetch logs');
      }
    } catch (err) {
      console.error('Error fetching conversation logs:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  // Filter logs based on company debug settings
  const filteredLogs = logs.filter(log => {
    // If debug logs disabled entirely, show nothing
    if (!debugSettings.enabled) return false;
    
    // If no filter specified, show all logs (default behavior)
    if (!debugSettings.visible_log_types || debugSettings.visible_log_types.length === 0) return true;
    
    // Filter by visible log types
    return debugSettings.visible_log_types.includes(log.log_type);
  });
  
  // Sort logs chronologically by timestamp (flat view)
  const sortedLogs = filteredLogs.sort((a, b) => {
    const timeA = new Date(a.timestamp).getTime();
    const timeB = new Date(b.timestamp).getTime();
    return timeA - timeB;
  });

  const getLogTypeIcon = (logType) => {
    switch (logType) {
      case 'message':
        return <InfoIcon fontSize="small" color="primary" />;
      case 'template_execution':
        return <CodeIcon fontSize="small" color="info" />;
      case 'template_matching':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'parameters':
        return <InfoIcon fontSize="small" color="info" />;
      case 'query_batch':
        return <FunctionsIcon fontSize="small" color="info" />;
      case 'sql_query':
        return <StorageIcon fontSize="small" color="primary" />;
      case 'sql_results':
        return <CheckCircleIcon fontSize="small" color="info" />;
      case 'ai_analysis':
        return <CodeIcon fontSize="small" color="secondary" />;
      case 'streaming':
        return <InfoIcon fontSize="small" color="info" />;
      case 'event':
        return <InfoIcon fontSize="small" color="default" />;
      case 'mcp_tool':
        return <StorageIcon fontSize="small" color="info" />;
      case 'request':
        return <InfoIcon fontSize="small" color="primary" />;
      case 'ai_prompt':
        return <CodeIcon fontSize="small" color="secondary" />;
      case 'ai_response':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'response':
      case 'final_response':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'function_call':
        return <FunctionsIcon fontSize="small" color="warning" />;
      case 'function_result':
        return <StorageIcon fontSize="small" color="info" />;
      case 'tool_call':
        return <StorageIcon fontSize="small" color="secondary" />;
      case 'tool_result':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'tool_error':
        return <ErrorIcon fontSize="small" color="error" />;
      case 'context_service':
        return <HistoryIcon fontSize="small" color="secondary" />;
      default:
        return <CodeIcon fontSize="small" />;
    }
  };

  const getLogTypeColor = (logType) => {
    switch (logType) {
      case 'message':
        return 'primary';
      case 'template_execution':
        return 'info';
      case 'template_matching':
        return 'success';
      case 'parameters':
        return 'info';
      case 'query_batch':
        return 'info';
      case 'sql_query':
        return 'primary';
      case 'sql_results':
        return 'info';
      case 'ai_analysis':
        return 'secondary';
      case 'streaming':
        return 'info';
      case 'event':
        return 'default';
      case 'mcp_tool':
        return 'info';
      case 'request':
        return 'primary';
      case 'ai_prompt':
        return 'secondary';
      case 'ai_response':
        return 'success';
      case 'response':
      case 'final_response':
        return 'success';
      case 'function_call':
        return 'warning';
      case 'function_result':
        return 'info';
      case 'tool_call':
        return 'secondary';
      case 'tool_result':
        return 'success';
      case 'tool_error':
        return 'error';
      case 'context_service':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const downloadLogsAsJSON = () => {
    try {
      const logsData = {
        conversation_id: conversationId,
        company_id: companyId,
        message_id: selectedMessageId,
        export_timestamp: new Date().toISOString(),
        total_logs: logs.length,
        logs: sortedLogs.map(log => ({
          message_id: log.message_id,
          log_type: log.log_type,
          log_subtype: log.log_subtype,
          content: log.content,
          metadata: log.metadata,
          timestamp: log.timestamp
        }))
      };
      
      const jsonString = JSON.stringify(logsData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `debug-logs-${conversationId}-${selectedMessageId || 'all'}-${Date.now()}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('[Debug Logs] Downloaded JSON logs:', logsData.total_logs, 'entries');
    } catch (error) {
      console.error('[Debug Logs] Error downloading JSON logs:', error);
      alert('Failed to download logs as JSON');
    }
  };
  
  const downloadLogsAsMarkdown = () => {
    try {
      let markdown = `# Debug Logs Export\n\n`;
      markdown += `**Conversation ID:** ${conversationId}\n`;
      markdown += `**Company ID:** ${companyId}\n`;
      markdown += `**Message ID:** ${selectedMessageId || 'All messages'}\n`;
      markdown += `**Export Date:** ${new Date().toISOString()}\n`;
      markdown += `**Total Logs:** ${logs.length}\n\n`;
      markdown += `---\n\n`;
      
      // Export logs in chronological order
      sortedLogs.forEach((log, idx) => {
          markdown += `### ${idx + 1}. ${log.log_type} (${log.log_subtype})\n\n`;
          markdown += `**Timestamp:** ${formatTimestamp(log.timestamp)}\n\n`;
          
          const content = log.content || {};
          
          // Format content based on log type
          switch (log.log_type) {
            case 'message':
              markdown += `**User Message:**\n\`\`\`\n${content.message || 'N/A'}\n\`\`\`\n\n`;
              break;
              
            case 'template_matching':
              markdown += `**Match Status:** ${content.matched ? '✅ Match Found' : '❌ No Match'}\n`;
              if (content.confidence !== undefined) {
                markdown += `**Confidence:** ${(content.confidence * 100).toFixed(0)}%\n`;
              }
              if (content.matched && content.template_name) {
                markdown += `**Template:** ${content.template_name}\n`;
                if (content.template_id) {
                  markdown += `**Template ID:** ${content.template_id}\n`;
                }
              }
              if (!content.matched && content.fallback_reason) {
                markdown += `**Fallback Reason:** ${content.fallback_reason}\n`;
              }
              if (content.match_method) {
                markdown += `**Match Method:** ${content.match_method}\n`;
              }
              markdown += `\n`;
              break;
              
            case 'parameters':
              markdown += `**Parameter Count:** ${content.parameter_count || 0}\n\n`;
              if (content.parameters && Object.keys(content.parameters).length > 0) {
                markdown += `**Parameter Values:**\n\`\`\`json\n${JSON.stringify(content.parameters, null, 2)}\n\`\`\`\n\n`;
              }
              break;
              
            case 'query_batch':
              markdown += `**Query Count:** ${content.query_count || 0}\n\n`;
              if (content.query_names && content.query_names.length > 0) {
                markdown += `**Queries in Batch:**\n`;
                content.query_names.forEach((name, idx) => {
                  markdown += `${idx + 1}. ${name}`;
                  if (content.query_ids && content.query_ids[idx]) {
                    markdown += ` (ID: ${content.query_ids[idx]})`;
                  }
                  markdown += `\n`;
                });
                markdown += `\n`;
              }
              break;
              
            case 'request':
              markdown += `**User Prompt:**\n\`\`\`\n${content.user_prompt || 'N/A'}\n\`\`\`\n\n`;
              if (content.schema_summary) {
                markdown += `**Database:** ${content.schema_summary.database}\n`;
                markdown += `**Tables:** ${content.schema_summary.tables?.join(', ') || 'N/A'}\n\n`;
              }
              break;
              
            case 'response':
              if (content.function_call_details) {
                markdown += `**Function Call:** ${content.function_call_details.name}\n`;
                markdown += `**Arguments:**\n\`\`\`json\n${JSON.stringify(content.function_call_details, null, 2)}\n\`\`\`\n\n`;
              }
              if (content.text_response) {
                markdown += `**Text Response:**\n\`\`\`\n${content.text_response}\n\`\`\`\n\n`;
              }
              if (log.metadata?.usage) {
                markdown += `**Token Usage:** Input: ${log.metadata.usage.input_tokens}, Output: ${log.metadata.usage.output_tokens}, Cached: ${log.metadata.usage.cached_input_tokens || 0}\n\n`;
              }
              break;
              
            case 'function_call':
              markdown += `**Function:** ${content.function}\n`;
              if (content.arguments?.query) {
                markdown += `**SQL Query:**\n\`\`\`sql\n${content.arguments.query}\n\`\`\`\n\n`;
              }
              break;
              
            case 'sql_query':
              if (content.template_name) {
                markdown += `**Template:** ${content.template_name}\n`;
              }
              markdown += `**SQL Query:**\n\`\`\`sql\n${content.query || 'N/A'}\n\`\`\`\n\n`;
              if (content.parameters && Object.keys(content.parameters).length > 0) {
                markdown += `**Parameters:**\n\`\`\`json\n${JSON.stringify(content.parameters, null, 2)}\n\`\`\`\n\n`;
              }
              if (log.log_subtype === 'query_error' && content.error_message) {
                markdown += `**Error:**\n\`\`\`\n${content.error_message}\n\`\`\`\n\n`;
              }
              break;
              
            case 'sql_results':
              if (content.template_name) {
                markdown += `**Template:** ${content.template_name}\n`;
              }
              markdown += `**Rows:** ${content.row_count || 0}\n`;
              if (log.metadata?.execution_time_ms) {
                markdown += `**Execution Time:** ${log.metadata.execution_time_ms}ms\n`;
              }
              if (log.metadata?.cached) {
                markdown += `**Cached:** ✅\n`;
              }
              markdown += `\n`;
              if (content.result_preview) {
                markdown += `**Result Preview:**\n\`\`\`json\n${content.result_preview.substring(0, 1000)}${content.result_preview.length > 1000 ? '...' : ''}\n\`\`\`\n\n`;
              }
              if (content.error) {
                markdown += `**Error:**\n\`\`\`\n${content.error}\n\`\`\`\n\n`;
              }
              break;
              
            case 'function_result':
              markdown += `**Success:** ${content.success ? '✅' : '❌'}\n`;
              markdown += `**Rows:** ${content.result_rows}\n`;
              markdown += `**Size:** ${(content.result_length / 1024).toFixed(1)} KB\n\n`;
              if (content.query) {
                markdown += `**SQL Query:**\n\`\`\`sql\n${content.query}\n\`\`\`\n\n`;
              }
              if (content.result_preview) {
                markdown += `**Result Preview:**\n\`\`\`json\n${content.result_preview.substring(0, 500)}${content.result_preview.length > 500 ? '...' : ''}\n\`\`\`\n\n`;
              }
              break;
              
            case 'mcp_tool':
              markdown += `**Tool:** ${content.tool_name || 'Unknown'}\n`;
              markdown += `**Type:** ${log.log_subtype === 'tool_start' ? 'Tool Start' : 'Tool Result'}\n`;
              if (content.context_database) {
                markdown += `**Database:** ${content.context_database}\n`;
              }
              if (log.log_subtype === 'tool_result') {
                markdown += `**Status:** ${content.success ? '✅ Success' : '❌ Failed'}\n`;
              }
              markdown += `\n`;
              if (log.log_subtype === 'tool_start' && content.tool_input) {
                markdown += `**Tool Input (${content.input_length || 0} chars):**\n\`\`\`\n${content.tool_input.substring(0, 1000)}${content.input_length > 1000 ? '...' : ''}\n\`\`\`\n\n`;
              }
              if (log.log_subtype === 'tool_result' && content.result_preview) {
                markdown += `**Tool Result (${content.result_length || 0} chars):**\n\`\`\`\n${content.result_preview.substring(0, 1000)}${content.result_length > 1000 ? '...' : ''}\n\`\`\`\n\n`;
              }
              if (content.error) {
                markdown += `**Error:**\n\`\`\`\n${content.error}\n\`\`\`\n\n`;
              }
              if (log.metadata?.execution_time_ms) {
                markdown += `**Execution Time:** ${log.metadata.execution_time_ms}ms\n\n`;
              }
              break;
              
            case 'tool_call':
              markdown += `**Tool:** ${content.tool_name}\n`;
              markdown += `**Database:** ${content.context_database}\n`;
              markdown += `**Input Length:** ${content.input_length} chars\n\n`;
              if (content.input_preview) {
                markdown += `**Input:**\n\`\`\`\n${content.input_preview}\n\`\`\`\n\n`;
              }
              break;
              
            case 'tool_result':
              markdown += `**Tool:** ${content.tool_name}\n`;
              markdown += `**Success:** ${content.success ? '✅' : '❌'}\n`;
              markdown += `**Execution Time:** ${content.execution_time?.toFixed(2)}s\n`;
              markdown += `**Result Size:** ${(content.result_length / 1024).toFixed(1)} KB\n\n`;
              if (content.result_preview) {
                markdown += `**Result Preview:**\n\`\`\`\n${content.result_preview.substring(0, 500)}${content.result_preview.length > 500 ? '...' : ''}\n\`\`\`\n\n`;
              }
              break;
              
            case 'tool_error':
              markdown += `**Tool:** ${content.tool_name}\n`;
              markdown += `**Execution Time:** ${content.execution_time?.toFixed(2)}s\n`;
              markdown += `**Error:**\n\`\`\`\n${content.error}\n\`\`\`\n\n`;
              break;
              
            case 'ai_analysis':
              markdown += `**Provider:** ${content.provider || 'unknown'}\n`;
              markdown += `**Status:** ${content.success ? '✅ Success' : '❌ Failed'}\n`;
              if (log.metadata?.model) {
                markdown += `**Model:** ${log.metadata.model}\n`;
              }
              markdown += `\n`;
              if (content.prompt_preview) {
                markdown += `**Prompt (${content.prompt_length || 0} chars):**\n\`\`\`\n${content.prompt_preview.substring(0, 1000)}${content.prompt_length > 1000 ? '...' : ''}\n\`\`\`\n\n`;
              }
              if (content.response_preview) {
                markdown += `**Response (${content.response_length || 0} chars):**\n\`\`\`\n${content.response_preview.substring(0, 1000)}${content.response_length > 1000 ? '...' : ''}\n\`\`\`\n\n`;
              }
              if (content.error) {
                markdown += `**Error:**\n\`\`\`\n${content.error}\n\`\`\`\n\n`;
              }
              if (log.metadata?.execution_time_ms) {
                markdown += `**Execution Time:** ${log.metadata.execution_time_ms}ms\n\n`;
              }
              break;
              
            case 'streaming':
              markdown += `**Event Type:** ${content.event_type || content.phase_name || log.log_subtype || 'unknown'}\n`;
              if (content.phase) {
                markdown += `**Phase:** ${content.phase}\n`;
              }
              if (content.status) {
                markdown += `**Status:** ${content.status}\n`;
              }
              if (content.phase_description) {
                markdown += `**Description:** ${content.phase_description}\n`;
              }
              markdown += `\n`;
              if (content.data_preview) {
                markdown += `**Event Data:**\n\`\`\`\n${content.data_preview.substring(0, 500)}${content.data_preview.length > 500 ? '...' : ''}\n\`\`\`\n\n`;
              }
              if (content.has_html) {
                markdown += `**Contains HTML:** ✅ (${content.html_length || 0} chars)\n\n`;
              }
              break;
              
            case 'event':
              markdown += `**Event Name:** ${content.event_name || 'Unknown Event'}\n`;
              if (content.event_description) {
                markdown += `**Description:** ${content.event_description}\n`;
              }
              markdown += `**Status:** ${content.success ? '✅ Success' : '❌ Failed'}\n`;
              if (log.metadata?.event_category) {
                markdown += `**Category:** ${log.metadata.event_category}\n`;
              }
              if (log.metadata?.source) {
                markdown += `**Source:** ${log.metadata.source}\n`;
              }
              markdown += `\n`;
              if (content.event_data && Object.keys(content.event_data).length > 0) {
                markdown += `**Event Data:**\n\`\`\`json\n${JSON.stringify(content.event_data, null, 2)}\n\`\`\`\n\n`;
              }
              if (content.error) {
                markdown += `**Error:**\n\`\`\`\n${content.error}\n\`\`\`\n\n`;
              }
              break;
              
            case 'final_response':
              markdown += `**Status:** ${content.status}\n`;
              if (log.metadata?.execution_time) {
                markdown += `**Execution Time:** ${log.metadata.execution_time.toFixed(2)}s\n`;
              }
              if (content.response_preview) {
                markdown += `**Response Preview:**\n${content.response_preview.substring(0, 300)}${content.response_preview.length > 300 ? '...' : ''}\n\n`;
              }
              break;
              
            default:
              markdown += `**Content:**\n\`\`\`json\n${JSON.stringify(content, null, 2)}\n\`\`\`\n\n`;
          }
          
          markdown += `---\n\n`;
      });
      
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `debug-logs-${conversationId}-${selectedMessageId || 'all'}-${Date.now()}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      console.log('[Debug Logs] Downloaded Markdown logs:', logs.length, 'entries');
    } catch (error) {
      console.error('[Debug Logs] Error downloading Markdown logs:', error);
      alert('Failed to download logs as Markdown');
    }
  };

  const renderLogContent = (log) => {
    const content = log.content || {};
    
    switch (log.log_type) {
      case 'message':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>💬 User Message:</strong>
            </Typography>
            <Paper sx={{ p: 2, bgcolor: 'primary.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
              <Typography variant="body1">
                {content.message || 'No message text'}
              </Typography>
            </Paper>
            {log.metadata && (
              <Stack spacing={1} direction="row" sx={{ mt: 1 }}>
                {log.metadata.template_matched && (
                  <Chip label="Template Matched" size="small" color="success" />
                )}
                {log.metadata.templateId && (
                  <Chip label={log.metadata.templateId} size="small" variant="outlined" />
                )}
                {log.metadata.templateName && (
                  <Chip label={log.metadata.templateName} size="small" variant="outlined" />
                )}
              </Stack>
            )}
          </Box>
        );
      
      case 'template_matching':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🎯 Template Matching:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.matched ? '✅ Match Found' : '❌ No Match'} 
                size="small" 
                color={content.matched ? 'success' : 'error'}
              />
              {content.confidence !== undefined && (
                <Chip 
                  label={`Confidence: ${(content.confidence * 100).toFixed(0)}%`} 
                  size="small" 
                  color={content.confidence >= 0.6 ? 'success' : 'warning'}
                />
              )}
              {content.match_method && (
                <Chip 
                  label={`Method: ${content.match_method}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {content.matched && content.template_name && (
              <Alert severity="success" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Template: {content.template_name}
                </Typography>
                {content.template_id && (
                  <Typography variant="caption" display="block">
                    ID: {content.template_id} | Type: {content.template_type || 'unknown'}
                  </Typography>
                )}
              </Alert>
            )}
            
            {!content.matched && content.fallback_reason && (
              <Alert severity="info">
                <Typography variant="caption">
                  <strong>Fallback Reason:</strong> {content.fallback_reason}
                </Typography>
              </Alert>
            )}
            
            {log.metadata?.keyword_matches && log.metadata.keyword_matches.length > 0 && (
              <Paper sx={{ p: 1, bgcolor: 'grey.50', mt: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Keyword Matches:
                </Typography>
                <Typography variant="caption">
                  {log.metadata.keyword_matches.join(', ')}
                </Typography>
              </Paper>
            )}
            
            {log.metadata?.llm_used && (
              <Chip label="LLM-Assisted Match" size="small" color="info" sx={{ mt: 1 }} />
            )}
          </Box>
        );
      
      case 'parameters':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>⚙️ Extracted Parameters:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 1 }}>
              <Chip 
                label={`${content.parameter_count || 0} parameters`} 
                size="small" 
                color="info"
              />
            </Stack>
            
            {content.parameters && Object.keys(content.parameters).length > 0 && (
              <Accordion sx={{ bgcolor: 'info.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📋 Parameter Values
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white' }}>
                    <Stack spacing={1}>
                      {Object.entries(content.parameters).map(([key, value]) => (
                        <Box key={key}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {key}:
                          </Typography>
                          <Typography variant="caption" component="pre" sx={{ ml: 1, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                            {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                          </Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {content.parameter_names && content.parameter_names.length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  <strong>Parameters:</strong> {content.parameter_names.join(', ')}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'query_batch':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📦 Multi-Query Batch:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={`${content.query_count || 0} queries`} 
                size="small" 
                color="info"
                icon={<FunctionsIcon />}
              />
              {log.metadata?.template_type && (
                <Chip 
                  label={log.metadata.template_type} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {content.query_names && content.query_names.length > 0 && (
              <Alert severity="info">
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Queries in Batch:
                </Typography>
                <Stack spacing={0.5}>
                  {content.query_names.map((name, idx) => (
                    <Typography key={idx} variant="caption" sx={{ display: 'flex', alignItems: 'center' }}>
                      {idx + 1}. {name}
                      {content.query_ids && content.query_ids[idx] && (
                        <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                          (ID: {content.query_ids[idx]})
                        </Typography>
                      )}
                    </Typography>
                  ))}
                </Stack>
              </Alert>
            )}
          </Box>
        );
      
      case 'ai_prompt':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📤 {log.log_subtype === 'claude_sql_generation' ? 'Claude Prompt (SQL Generation)' : 
                        log.log_subtype === 'claude_analysis' ? 'Claude Prompt (Analysis)' : 
                        log.log_subtype === 'gemini_function_call' ? 'Gemini Prompt (Function Calling)' :
                        'AI Prompt'}:</strong>
            </Typography>
            
            {/* Show full prompt */}
            {content.full_prompt && (
              <Accordion sx={{ bgcolor: 'blue.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📝 Full Prompt ({content.prompt_length?.toLocaleString()} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 500, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.full_prompt}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Show metadata */}
            {log.metadata && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  Model: {content.model} | Max Tokens: {log.metadata.max_tokens} | 
                  {log.metadata.has_schema_cache && ' Schema Cached ✅'}
                  {log.metadata.has_analysis_cache && ' Analysis Instructions Cached ✅'}
                  {content.purpose && ` | Purpose: ${content.purpose}`}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'sql_query':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🔍 SQL Query Execution{content.template_name ? `: ${content.template_name}` : ''}:</strong>
            </Typography>
            
            {/* Show full SQL query */}
            {content.query && (
              <Accordion sx={{ bgcolor: 'blue.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📝 SQL Query ({content.sql_length?.toLocaleString() || content.query.length} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 500, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.query}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Show parameters if available */}
            {content.parameters && Object.keys(content.parameters).length > 0 && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  <strong>Parameters:</strong> {JSON.stringify(content.parameters)}
                </Typography>
              </Alert>
            )}
            
            {/* Show error if query failed */}
            {log.log_subtype === 'query_error' && content.error_message && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {content.error_message}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'sql_results':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📊 SQL Results{content.template_name ? `: ${content.template_name}` : ''}:</strong>
            </Typography>
            
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={`${content.row_count || 0} rows`} 
                size="small" 
                color={content.success ? 'success' : 'error'}
              />
              {log.metadata?.execution_time_ms && (
                <Chip 
                  label={`${log.metadata.execution_time_ms}ms`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {log.metadata?.cached && (
                <Chip 
                  label="Cached ✅" 
                  size="small" 
                  color="success"
                />
              )}
            </Stack>
            
            {/* Show result preview */}
            {content.result_preview && (
              <Accordion sx={{ bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📄 Result Preview ({content.result_size_bytes?.toLocaleString() || 0} bytes)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.result_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Show error if query failed */}
            {content.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption">
                  {content.error}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'ai_response':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📥 {log.log_subtype === 'claude_sql_generation' ? 'Claude Response (SQL Queries)' : 
                        log.log_subtype === 'claude_analysis' ? 'Claude Response (Analysis)' : 
                        log.log_subtype === 'gemini_function_call' ? 'Gemini Response (Function Call or Text)' :
                        'AI Response'}:</strong>
            </Typography>
            
            {/* Show full response */}
            {content.full_response && (
              <Accordion sx={{ bgcolor: 'green.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    💬 Full Response ({content.response_length?.toLocaleString()} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 500, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.full_response}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Token usage with cache savings */}
            {log.metadata && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  <strong>Tokens:</strong> Input: {(log.metadata.input_tokens || log.metadata.prompt_tokens)?.toLocaleString()} | 
                  Output: {(log.metadata.output_tokens || log.metadata.completion_tokens)?.toLocaleString()}
                  {/* Claude uses cache_read_tokens, Gemini uses cached_input_tokens */}
                  {(log.metadata.cache_read_tokens > 0 || log.metadata.cached_input_tokens > 0) && (
                    <span style={{color: 'green', fontWeight: 'bold'}}>
                      {' '}| Cached Read: {(log.metadata.cache_read_tokens || log.metadata.cached_input_tokens)?.toLocaleString()} 💰 (90% savings!)
                    </span>
                  )}
                  {log.metadata.cache_creation_tokens > 0 && (
                    <span> | Cache Created: {log.metadata.cache_creation_tokens?.toLocaleString()}</span>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'ai_analysis':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🧠 AI Analysis:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.provider || 'unknown'} 
                size="small" 
                color="secondary"
              />
              <Chip 
                label={content.success ? '✅ Success' : '❌ Failed'} 
                size="small" 
                color={content.success ? 'success' : 'error'}
              />
              {log.metadata?.model && (
                <Chip 
                  label={log.metadata.model} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {/* Prompt */}
            {content.prompt_preview && (
              <Accordion sx={{ bgcolor: 'blue.50', mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📝 Prompt ({content.prompt_length?.toLocaleString() || 0} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.prompt_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Response */}
            {content.response_preview && (
              <Accordion sx={{ bgcolor: 'green.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    💬 Response ({content.response_length?.toLocaleString() || 0} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.response_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Error */}
            {content.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {content.error}
                </Typography>
              </Alert>
            )}
            
            {/* Metadata */}
            {log.metadata && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  {log.metadata.execution_time_ms && `Execution Time: ${log.metadata.execution_time_ms}ms`}
                  {log.metadata.tools_used && log.metadata.tools_used.length > 0 && ` | Tools: ${log.metadata.tools_used.join(', ')}`}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'streaming':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🌊 Streaming Event:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.event_type || content.phase_name || log.log_subtype || 'unknown'} 
                size="small" 
                color="info"
              />
              {content.phase && (
                <Chip 
                  label={`Phase: ${content.phase}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {content.status && (
                <Chip 
                  label={content.status} 
                  size="small" 
                  color={content.status === 'completed' ? 'success' : 'default'}
                />
              )}
            </Stack>
            
            {content.phase_description && (
              <Alert severity="info" sx={{ mb: 1 }}>
                <Typography variant="caption">
                  {content.phase_description}
                </Typography>
              </Alert>
            )}
            
            {content.data_preview && (
              <Accordion sx={{ bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📋 Event Data
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.data_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {(content.has_html || content.html_length) && (
              <Stack spacing={1} direction="row" sx={{ mt: 1 }}>
                {content.has_html && (
                  <Chip label="Contains HTML" size="small" variant="outlined" />
                )}
                {content.html_length && (
                  <Chip label={`HTML: ${content.html_length} chars`} size="small" variant="outlined" />
                )}
              </Stack>
            )}
            
            {log.metadata?.execution_time_ms && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Execution Time: {log.metadata.execution_time_ms}ms
              </Typography>
            )}
          </Box>
        );
      
      case 'event':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📣 Event: {content.event_name || 'Unknown Event'}</strong>
            </Typography>
            
            {content.event_description && (
              <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
                {content.event_description}
              </Typography>
            )}
            
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.success ? '✅ Success' : '❌ Failed'} 
                size="small" 
                color={content.success ? 'success' : 'error'}
              />
              {log.metadata?.event_category && (
                <Chip 
                  label={log.metadata.event_category} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {log.metadata?.source && (
                <Chip 
                  label={`Source: ${log.metadata.source}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {log.metadata?.execution_time_ms && (
                <Chip 
                  label={`${log.metadata.execution_time_ms}ms`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {content.event_data && Object.keys(content.event_data).length > 0 && (
              <Accordion sx={{ bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📋 Event Data
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {JSON.stringify(content.event_data, null, 2)}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {content.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption">
                  {content.error}
                </Typography>
              </Alert>
            )}
          </Box>
        );
      
      case 'template_execution':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>⚙️ Template Execution:</strong>
            </Typography>
            <Stack spacing={1}>
              {content.templateName && (
                <Alert severity="info" icon={<CodeIcon />}>
                  <Typography variant="body2">
                    <strong>Template:</strong> {content.templateName}
                  </Typography>
                </Alert>
              )}
              {content.templateId && (
                <Chip label={`ID: ${content.templateId}`} size="small" variant="outlined" />
              )}
              {content.phase && (
                <Chip label={`Phase: ${content.phase}`} size="small" color="info" />
              )}
            </Stack>
            {log.metadata && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Response Time: {log.metadata.responseTime}ms | Confidence: {(log.metadata.confidence * 100).toFixed(0)}%
              </Typography>
            )}
          </Box>
        );
      
      case 'request':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📤 Request to Gemini:</strong>
            </Typography>
            <Stack spacing={1}>
              <Chip 
                label={`History Length: ${content.history_length || 0}`} 
                size="small" 
                variant="outlined" 
              />
              <Chip 
                label={content.has_schema_cache ? 'Using Schema Cache ✅' : 'No Cache'} 
                size="small" 
                color={content.has_schema_cache ? 'success' : 'default'}
              />
              <Chip 
                label={`Functions: ${content.functions_available || 0}`} 
                size="small" 
              />
            </Stack>
            
            {/* User Prompt */}
            {content.user_prompt && (
              <Accordion sx={{ mt: 2, bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>💬 User Prompt</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {content.user_prompt}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Schema Summary */}
            {content.schema_summary && (
              <Accordion sx={{ mt: 1, bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>🗄️ Database Schema</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={1}>
                    <Alert severity="info" icon={<StorageIcon />}>
                      <Typography variant="body2">
                        <strong>Database:</strong> {content.schema_summary.database}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Tables:</strong> {content.schema_summary.tables?.join(', ')}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Schema Size:</strong> {(content.schema_summary.full_schema_length / 1024).toFixed(1)} KB
                      </Typography>
                    </Alert>
                    {/* Display full schema (stored as cacheable_schema_full) */}
                    {(content.cacheable_schema_full || content.cacheable_schema) && (
                      <Paper sx={{ p: 1, bgcolor: 'white', maxHeight: 600, overflow: 'auto' }}>
                        <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                          {content.cacheable_schema_full || content.cacheable_schema}
                        </Typography>
                        {content.schema_summary.full_schema_length > 50000 && (
                          <Alert severity="warning" sx={{ mt: 1 }}>
                            <Typography variant="caption">
                              ⚠️ Schema exceeds 50KB and may be truncated in storage
                            </Typography>
                          </Alert>
                        )}
                      </Paper>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Function Definitions */}
            {content.function_definitions && content.function_definitions.length > 0 && (
              <Accordion sx={{ mt: 1, bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>🔧 Available Functions ({content.function_definitions.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 1, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {JSON.stringify(content.function_definitions, null, 2)}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Conversation History */}
            {content.conversation_history && content.conversation_history.length > 0 && (
              <Accordion sx={{ mt: 1, bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>💭 Conversation History ({content.conversation_history.length} messages)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 1, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {JSON.stringify(content.conversation_history, null, 2)}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {log.metadata && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Max Tokens: {log.metadata.max_tokens} | Temp: {log.metadata.temperature}
              </Typography>
            )}
          </Box>
        );

      case 'response':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📥 Gemini Response:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap">
              <Chip 
                label={content.has_function_call ? 'Function Call 🔧' : 'Text Response 💬'} 
                size="small" 
                color={content.has_function_call ? 'warning' : 'success'}
              />
              <Chip 
                label={`Finish: ${content.finish_reason || 'unknown'}`} 
                size="small" 
                color={content.finish_reason === 'MAX_TOKENS' ? 'error' : 'default'}
              />
              {content.text_length > 0 && (
                <Chip 
                  label={`Text: ${content.text_length} chars`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {/* Function Call Details */}
            {content.function_call_details && (
              <Accordion sx={{ mt: 2, bgcolor: 'warning.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>🔧 Function Call: {content.function_call_details.name}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {JSON.stringify(content.function_call_details, null, 2)}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Text Response */}
            {content.text_response && (
              <Accordion sx={{ mt: 1, bgcolor: 'success.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>💬 Text Response ({content.text_length} chars)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {content.text_response}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {log.metadata?.usage && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  <strong>Token Usage:</strong> Input: {log.metadata.usage.input_tokens?.toLocaleString() || 0} | 
                  Output: {log.metadata.usage.output_tokens?.toLocaleString() || 0} | 
                  Total: {log.metadata.usage.total_tokens?.toLocaleString() || 0}
                  {log.metadata.usage.cached_input_tokens > 0 && (
                    <span style={{color: 'green', fontWeight: 'bold'}}> | Cached: {log.metadata.usage.cached_input_tokens.toLocaleString()} (💰 90% savings!)</span>
                  )}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 'function_call':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>Function: {content.function}</strong>
            </Typography>
            {content.arguments?.query && (
              <Paper sx={{ p: 1, bgcolor: 'grey.100', mt: 1, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {content.arguments.query}
                </Typography>
              </Paper>
            )}
            {content.arguments?.explanation && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">{content.arguments.explanation}</Typography>
              </Alert>
            )}
          </Box>
        );

      case 'mcp_tool':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🔧 MCP Tool {log.log_subtype === 'tool_start' ? 'Start' : 'Result'}:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.tool_name || 'Unknown Tool'} 
                size="small" 
                color={log.log_subtype === 'tool_start' ? 'info' : (content.success ? 'success' : 'error')}
                icon={<StorageIcon />}
              />
              {log.log_subtype === 'tool_result' && content.success !== undefined && (
                <Chip 
                  label={content.success ? '✅ Success' : '❌ Failed'} 
                  size="small" 
                  color={content.success ? 'success' : 'error'}
                />
              )}
              {content.context_database && (
                <Chip 
                  label={`DB: ${content.context_database}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {log.metadata?.execution_time_ms && (
                <Chip 
                  label={`${log.metadata.execution_time_ms}ms`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {/* Tool Input (for tool_start) */}
            {log.log_subtype === 'tool_start' && content.tool_input && (
              <Accordion sx={{ bgcolor: 'blue.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📝 Tool Input ({content.input_length?.toLocaleString() || 0} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.tool_input}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Tool Result (for tool_result) */}
            {log.log_subtype === 'tool_result' && content.result_preview && (
              <Accordion sx={{ bgcolor: 'green.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    📊 Tool Result ({content.result_length?.toLocaleString() || 0} chars)
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 400, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.result_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Error */}
            {content.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {content.error}
                </Typography>
              </Alert>
            )}
            
            {log.metadata?.mcp_enabled && (
              <Chip label="MCP Enabled" size="small" color="success" sx={{ mt: 1 }} />
            )}
          </Box>
        );

      case 'function_result':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>📊 Query Result:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.success ? 'Success ✅' : 'Failed ❌'} 
                size="small" 
                color={content.success ? 'success' : 'error'}
              />
              {content.result_rows && (
                <Chip 
                  label={`Rows: ${typeof content.result_rows === 'number' ? content.result_rows.toLocaleString() : content.result_rows}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {content.result_length && (
                <Chip 
                  label={`Size: ${(content.result_length / 1024).toFixed(1)} KB`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {/* Full SQL Query */}
            {content.query && (
              <Accordion sx={{ mb: 1, bgcolor: 'info.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>📝 SQL Query ({log.metadata?.query_length || content.query.length} chars)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {content.query}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Full Query Result */}
            {content.result_full && (
              <Accordion sx={{ bgcolor: 'success.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>📊 Complete Result ({content.result_length?.toLocaleString() || 0} chars)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 500, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.result_full}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Result Preview (fallback) */}
            {!content.result_full && content.result_preview && (
              <Paper sx={{ p: 2, bgcolor: 'grey.100', maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>Result Preview:</Typography>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {content.result_preview}
                </Typography>
              </Paper>
            )}
            
            {log.metadata?.explanation && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  <strong>Explanation:</strong> {log.metadata.explanation}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 'tool_call':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🔧 MCP Tool Call:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.tool_name || 'Unknown Tool'} 
                size="small" 
                color="secondary"
                icon={<StorageIcon />}
              />
              {content.input_length && (
                <Chip 
                  label={`Input: ${content.input_length} chars`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {content.context_database && (
                <Chip 
                  label={`DB: ${content.context_database}`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {/* Tool Input */}
            {content.input_preview && (
              <Accordion sx={{ bgcolor: 'grey.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>📝 Tool Input ({content.input_length} chars)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                      {content.input_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        );

      case 'tool_result':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>✅ MCP Tool Result:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.tool_name || 'Unknown Tool'} 
                size="small" 
                color="success"
                icon={<CheckCircleIcon />}
              />
              {content.success !== undefined && (
                <Chip 
                  label={content.success ? 'Success ✅' : 'Failed ❌'} 
                  size="small" 
                  color={content.success ? 'success' : 'error'}
                />
              )}
              {content.execution_time && (
                <Chip 
                  label={`${content.execution_time.toFixed(2)}s`} 
                  size="small" 
                  variant="outlined"
                />
              )}
              {content.result_length && (
                <Chip 
                  label={`Result: ${(content.result_length / 1024).toFixed(1)} KB`} 
                  size="small" 
                  variant="outlined"
                />
              )}
            </Stack>
            
            {/* Tool Result */}
            {content.result_preview && (
              <Accordion sx={{ bgcolor: 'success.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>📊 Tool Result ({content.result_length?.toLocaleString() || 0} chars)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white', maxHeight: 500, overflow: 'auto' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                      {content.result_preview}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {log.metadata?.is_mcp_server !== undefined && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  Tool Type: {log.metadata.is_mcp_server ? 'MCP Server' : 'Legacy Tool'}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 'tool_error':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>❌ MCP Tool Error:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              <Chip 
                label={content.tool_name || 'Unknown Tool'} 
                size="small" 
                color="error"
                icon={<ErrorIcon />}
              />
              {content.execution_time && (
                <Chip 
                  label={`Failed after ${content.execution_time.toFixed(2)}s`} 
                  size="small" 
                  variant="outlined"
                  color="error"
                />
              )}
            </Stack>
            
            {/* Error Message */}
            {content.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                  {content.error}
                </Typography>
              </Alert>
            )}
          </Box>
        );

      case 'final_response':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>✅ Final Response:</strong>
            </Typography>
            <Stack spacing={1} direction="row" flexWrap="wrap">
              {content.status && (
                <Chip label={content.status} size="small" color={content.status === 'completed' ? 'success' : 'default'} />
              )}
              {content.htmlLength !== undefined && (
                <Chip label={`HTML: ${content.htmlLength} chars`} size="small" />
              )}
              {content.original_length && (
                <Chip label={`${content.original_length} chars`} size="small" />
              )}
              {content.was_converted && (
                <Chip label="Markdown→HTML" size="small" color="warning" />
              )}
            </Stack>
            {log.metadata && (
              <Alert severity="success" sx={{ mt: 1 }}>
                <Typography variant="caption">
                  {log.metadata.iterations && `Completed in ${log.metadata.iterations} iterations`}
                  {log.metadata.tools_used && ` using ${log.metadata.tools_used} tools`}
                  {log.metadata.execution_time && ` (${log.metadata.execution_time?.toFixed(2)}s)`}
                  {log.metadata.responseTime && `Response Time: ${log.metadata.responseTime}ms`}
                  {log.metadata.streaming !== undefined && ` | Streaming: ${log.metadata.streaming ? 'Yes' : 'No'}`}
                </Typography>
              </Alert>
            )}
            {content.response_preview && (
              <Paper sx={{ p: 1, bgcolor: 'grey.100', mt: 1, maxHeight: 200, overflow: 'auto' }}>
                <Typography variant="caption" component="div">
                  <div dangerouslySetInnerHTML={{ __html: sanitizeInlineHTML(content.response_preview) }} />
                </Typography>
              </Paper>
            )}
          </Box>
        );

      case 'context_service':
        return (
          <Box>
            <Typography variant="body2" gutterBottom>
              <strong>🔄 Conversation Context {log.log_subtype === 'context_saved' ? 'Saved' : 
                                             log.log_subtype === 'context_retrieved' ? 'Retrieved' :
                                             log.log_subtype === 'context_extracted' ? 'Extracted (AI)' :
                                             log.log_subtype === 'context_cleared' ? 'Cleared' : 'Operation'}:</strong>
            </Typography>
            
            <Stack spacing={1} direction="row" flexWrap="wrap" sx={{ mb: 2 }}>
              {/* Context Saved */}
              {log.log_subtype === 'context_saved' && (
                <>
                  <Chip label="Context Saved" size="small" color="success" />
                  {content.has_sql && <Chip label="SQL ✓" size="small" variant="outlined" />}
                  {content.has_results && <Chip label="Results ✓" size="small" variant="outlined" />}
                  {content.has_template && <Chip label={`Template: ${content.template_name || 'Yes'}`} size="small" color="info" />}
                  {content.has_extracted_context && <Chip label="AI Extracted ✓" size="small" color="secondary" />}
                </>
              )}
              
              {/* Context Retrieved */}
              {log.log_subtype === 'context_retrieved' && (
                <>
                  {content.has_context ? (
                    <>
                      <Chip label="Context Retrieved" size="small" color="success" />
                      {content.message_count > 0 && <Chip label={`${content.message_count} messages`} size="small" variant="outlined" />}
                      {content.context_length > 0 && <Chip label={`${content.context_length} chars`} size="small" variant="outlined" />}
                      {content.template_used && <Chip label={`Template: ${content.template_used}`} size="small" color="info" />}
                    </>
                  ) : (
                    <>
                      <Chip label="No Previous Context" size="small" color="default" />
                      {content.reason && <Chip label={content.reason} size="small" variant="outlined" />}
                    </>
                  )}
                </>
              )}
              
              {/* Context Extracted (AI) */}
              {log.log_subtype === 'context_extracted' && (
                <>
                  <Chip label="AI Extraction" size="small" color="secondary" icon={<HistoryIcon />} />
                  {content.has_time_period && <Chip label="Time Period ✓" size="small" variant="outlined" />}
                  {content.filter_count > 0 && <Chip label={`${content.filter_count} filters`} size="small" variant="outlined" />}
                  {content.metric_count > 0 && <Chip label={`${content.metric_count} metrics`} size="small" variant="outlined" />}
                  {content.intent && <Chip label={content.intent} size="small" color="primary" />}
                  {content.extractor && <Chip label={content.extractor} size="small" variant="outlined" />}
                </>
              )}
              
              {/* Context Cleared */}
              {log.log_subtype === 'context_cleared' && (
                <Chip label="Context Cleared" size="small" color="warning" />
              )}
              
              {/* Errors */}
              {(log.log_subtype === 'context_retrieval_error' || log.log_subtype === 'context_extraction_error') && (
                <>
                  <Chip label="Error" size="small" color="error" icon={<ErrorIcon />} />
                  {content.fallback && <Chip label={`Fallback: ${content.fallback}`} size="small" variant="outlined" />}
                </>
              )}
            </Stack>
            
            {/* Show extracted context details if available */}
            {log.log_subtype === 'context_extracted' && content.extracted_context && (
              <Accordion sx={{ mt: 1, bgcolor: 'secondary.50' }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>📊 Extracted Context (Structured)</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Paper sx={{ p: 2, bgcolor: 'white' }}>
                    <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {JSON.stringify(content.extracted_context, null, 2)}
                    </Typography>
                  </Paper>
                </AccordionDetails>
              </Accordion>
            )}
            
            {/* Show error details */}
            {content.error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Error:</Typography>
                <Typography variant="caption">{content.error}</Typography>
                {content.error_type && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>Type: {content.error_type}</Typography>}
              </Alert>
            )}
            
            {/* Show source */}
            {content.source && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Source: {content.source}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <Paper sx={{ p: 1, bgcolor: 'grey.100', maxHeight: 200, overflow: 'auto' }}>
            <Typography variant="caption" component="pre">
              {JSON.stringify(content, null, 2)}
            </Typography>
          </Paper>
        );
    }
  };

  if (!open) return null;
  
  // Show message if debug logs are disabled for this company
  if (!debugSettings.enabled) {
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          mt: 2, 
          mb: 2, 
          border: '2px solid',
          borderColor: 'warning.main'
        }}
      >
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <BugReportIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Debug Logs Disabled
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Debug logs have been disabled for your organization. Contact your administrator to enable them.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper 
      elevation={3} 
      sx={{ 
        mt: 2, 
        mb: 2, 
        maxHeight: '600px', 
        overflow: 'auto',
        border: '2px solid',
        borderColor: 'primary.main'
      }}
    >
      <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BugReportIcon />
          <Typography variant="h6">
            Debug Logs
          </Typography>
          {logs.length > 0 && (
            <Badge badgeContent={logs.length} color="secondary" />
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Download as JSON (for analysis)">
            <IconButton 
              size="small" 
              onClick={downloadLogsAsJSON} 
              sx={{ color: 'white' }}
              disabled={logs.length === 0}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download as Markdown (for Claude)">
            <IconButton 
              size="small" 
              onClick={downloadLogsAsMarkdown} 
              sx={{ color: 'white' }}
              disabled={logs.length === 0}
            >
              <ArticleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh logs">
            <IconButton size="small" onClick={fetchLogs} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={{ color: 'white', ml: 1 }}>
              ✕
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ p: 2 }}>
        {/* Message selector - show when we have logs OR messages */}
        {(messageList.length > 0 || logs.length > 0) && (
          <Paper sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', mb: 2 }}>
              💬 Filter Logs by Message
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Message</InputLabel>
              <Select 
                label="Message" 
                value={selectedMessageId || ''} 
                onChange={(e) => setSelectedMessageId(e.target.value)}
                sx={{ bgcolor: 'white' }}
              >
                <MenuItem value="">
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      All messages (entire conversation)
                    </Typography>
                  </Box>
                </MenuItem>
                {messageList.map((m, index) => (
                  <MenuItem key={m.message_id} value={m.message_id}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {m.message || '[no text]'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        🕒 {formatTimestamp(m.timestamp)} (Message #{messageList.length - index})
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Showing logs for: {selectedMessageId ? (messageList.find(m => m.message_id === selectedMessageId)?.message || 'Select a message') : 'All messages'}
            </Typography>
          </Paper>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && logs.length === 0 && (
          <Alert severity="info" icon={<InfoIcon />}>
            {messageList.length === 0 ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  ✅ Conversation created successfully
                </Typography>
                <Typography variant="body2">
                  No messages sent yet. Debug logs will appear here once you send your first message.
                </Typography>
              </Box>
            ) : (
              'No debug logs found for this selection.'
            )}
          </Alert>
        )}

        {!loading && !error && logs.length > 0 && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="caption">
                Showing {logs.length} log entries in chronological order
              </Typography>
            </Alert>

            {/* Flat chronological view - timestamp on left */}
            <Stack spacing={1}>
              {sortedLogs.map((log, index) => (
                <Card key={index} sx={{ 
                  border: '1px solid',
                  borderColor: 'grey.300',
                  '&:hover': { boxShadow: 2 }
                }}>
                  <CardContent>
                    <Stack direction="row" spacing={2} alignItems="flex-start">
                      {/* Timestamp on left - fixed width */}
                      <Box sx={{ minWidth: 180, flexShrink: 0 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            color: 'text.secondary',
                            fontWeight: 500
                          }}
                        >
                          {formatTimestamp(log.timestamp)}
                        </Typography>
                      </Box>
                      
                      {/* Log level/type indicator */}
                      <Box sx={{ minWidth: 140, flexShrink: 0 }}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {getLogTypeIcon(log.log_type)}
                          <Chip 
                            label={log.log_type}
                            size="small"
                            color={getLogTypeColor(log.log_type)}
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {log.log_subtype}
                        </Typography>
                      </Box>
                      
                      {/* Log content - takes remaining space */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        {renderLogContent(log)}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default ConversationDebugLogs;
